import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { trackUsage, startDwellTimer } from '../lib/telemetry';
import {
  DocumentTextIcon,
  ChartBarIcon,
  CalculatorIcon,
  CheckCircleIcon,
  FolderIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';
import PositiveGoalCard from '../components/learn/PositiveGoalCard';

const Learn: React.FC = () => {
  const cards = [
    {
      id: 1,
      title: 'Do I need a GST/HST number?',
      icon: DocumentTextIcon,
      content: (
        <div className="space-y-3">
          <p className="text-gray-700">
            <strong>Yes.</strong> Rideshare drivers must register for GST/HST before your first trip
            even under $30k.
          </p>
          <p className="text-gray-700">
            Uber collects tax from riders, but you report and remit it. We handle the math for your
            province.
          </p>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <div className="flex items-start">
              <InformationCircleIcon className="h-5 w-5 text-blue-600 mt-0.5 mr-2 flex-shrink-0" />
              <p className="text-sm text-blue-800">
                <strong>Rideshare registration mandatory since July 1, 2017.</strong>
              </p>
            </div>
          </div>
        </div>
      ),
    },
    {
      id: 2,
      title: 'What we track for you',
      icon: ChartBarIcon,
      content: (
        <div className="space-y-3">
          <ul className="space-y-2 text-gray-700">
            <li className="flex items-start">
              <CheckCircleIcon className="h-5 w-5 text-green-600 mt-0.5 mr-2 flex-shrink-0" />
              <span>GST/HST collected on fares (from your sessions)</span>
            </li>
            <li className="flex items-start">
              <CheckCircleIcon className="h-5 w-5 text-green-600 mt-0.5 mr-2 flex-shrink-0" />
              <span>Input Tax Credits (ITCs) on eligible purchases (from your expenses)</span>
            </li>
            <li className="flex items-start">
              <CheckCircleIcon className="h-5 w-5 text-green-600 mt-0.5 mr-2 flex-shrink-0" />
              <span>CRA return lines (90–115) with Exact vs Rounded view</span>
            </li>
          </ul>
        </div>
      ),
    },
    {
      id: 3,
      title: 'Business-Use % (BUP)',
      icon: CalculatorIcon,
      content: (
        <div className="space-y-3">
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
            <p className="text-center font-mono text-lg text-gray-900">
              BUP = business km / total km
            </p>
          </div>
          <div className="space-y-2 text-gray-700">
            <p>
              <strong>Problem:</strong> = Uber only reports rider km, not dead km.
            </p>
            <p>
              <strong>Business km</strong> = rider km + dead km (app on / going to pickup / waiting
              in hot zones)
            </p>
            <p>
              <strong>Not business:</strong> driving home after logout
            </p>
          </div>
          <div className="bg-green-50 border border-green-200 rounded-lg p-3">
            <p className="text-sm text-green-800">
              <strong>Tip:</strong> Log odometer at start/end of each session. We'll compute BUP for
              you.
            </p>
          </div>
        </div>
      ),
    },
    {
      id: 4,
      title: 'Easy wins vs. caution',
      icon: CheckCircleIcon,
      content: (
        <div className="space-y-4">
          <div>
            <h4 className="font-medium text-green-900 mb-2">Usually claimable ITCs:</h4>
            <ul className="text-sm text-gray-700 space-y-1">
              <li>• Fuel, maintenance, washes, mounts/chargers</li>
              <li>• Parking, tolls, phone (business %)</li>
              <li>• Insurance (service fee GST)</li>
            </ul>
          </div>
          <div>
            <h4 className="font-medium text-orange-900 mb-2">Caution:</h4>
            <ul className="text-sm text-gray-700 space-y-1">
              <li>
                • Meals & entertainment (50% rule applies only if qualified; no ITC by default)
              </li>
              <li>• Financing/lease: limits + BUP apply</li>
            </ul>
          </div>
        </div>
      ),
    },
    {
      id: 5,
      title: 'Meals & Entertainment — Very High Audit Risk',
      icon: CheckCircleIcon,
      content: (
        <div className="space-y-3">
          <div id="meals-entertainment" className="bg-red-50 border border-red-200 rounded-lg p-3">
            <div className="flex items-start">
              <InformationCircleIcon className="h-5 w-5 text-red-600 mt-0.5 mr-2 flex-shrink-0" />
              <p className="text-sm text-red-800">
                <strong>Our policy: No ITC on M&E.</strong> We deliberately restrict ITCs to 0 to
                avoid high-risk claims for rideshare workflows.
              </p>
            </div>
          </div>
          <div className="space-y-2 text-gray-700">
            <p className="text-sm">
              <strong>Income deduction:</strong> $0 by default (personal expense).
            </p>
            <p className="text-sm">
              <strong>Narrow exception for rideshare:</strong> Overnight trip &gt; 100 miles / 160
              km from home, hotel stay, documented business meeting → may allow 50% income deduction
              (still ITC = 0).
            </p>
            <p className="text-sm">
              <strong>CRA baseline (for maintainers):</strong> CRA provides methods where up to 50%
              ITC on M&E may be computed; we intentionally restrict ITCs to 0 in this app.
            </p>
          </div>
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
            <p className="text-xs text-gray-600">
              <strong>Note:</strong> Laws update; we maintain current rules and will revise if
              ground truth changes.
            </p>
          </div>
        </div>
      ),
    },
    {
      id: 6,
      title: 'Records to keep (6 years)',
      icon: FolderIcon,
      content: (
        <div className="space-y-3">
          <ul className="space-y-2 text-gray-700">
            <li className="flex items-start">
              <CheckCircleIcon className="h-5 w-5 text-blue-600 mt-0.5 mr-2 flex-shrink-0" />
              <span>Receipts/invoices, mileage logs, odometer start/end</span>
            </li>
            <li className="flex items-start">
              <CheckCircleIcon className="h-5 w-5 text-blue-600 mt-0.5 mr-2 flex-shrink-0" />
              <span>Phone bills %, Uber summaries</span>
            </li>
          </ul>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-sm text-blue-800">
              We store receipts and keep your BUP consistent across GST/HST and income reporting.
            </p>
          </div>
        </div>
      ),
    },
  ];

  useEffect(() => {
    // Track page view and dwell time
    trackUsage('usage_page_view', { page: 'Learn' }).catch(console.warn);
    const stop = startDwellTimer('Learn');
    return stop;
  }, []);

  return (
    <div className="mx-auto max-w-6xl px-4 md:px-6 lg:px-8">
      <div className="space-y-8">
        {/* Branding Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center pt-4"
        >
          <div className="inline-flex items-center justify-center space-x-3 mb-4">
            <div className="h-12 w-12 bg-gradient-to-br from-blue-600 to-indigo-700 rounded-xl flex items-center justify-center shadow-lg">
              <span className="text-white font-bold text-xl">A</span>
            </div>
            <h1 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-blue-600 to-indigo-700 bg-clip-text text-transparent">
              AutoTaxPilot™
            </h1>
          </div>
          <p className="text-gray-600 text-sm font-medium">
            Built for Uber Drivers • Sole Proprietors • Canadian Tax Compliance
          </p>
        </motion.div>

        <PositiveGoalCard />

        {/* Header */}
        <div className="text-center">
          <motion.h2
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-3xl md:text-4xl font-bold text-gray-900"
          >
            Learn the Basics
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="mt-3 text-lg text-gray-600 max-w-3xl mx-auto"
          >
            Everything you need to know about rideshare tax compliance in Canada
          </motion.p>
        </div>

        {/* Compliance Banner */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-gradient-to-r from-amber-50 to-yellow-50 border-2 border-amber-200 rounded-xl p-5 shadow-sm"
        >
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <div className="h-10 w-10 bg-amber-100 rounded-full flex items-center justify-center">
                <InformationCircleIcon className="h-6 w-6 text-amber-600" />
              </div>
            </div>
            <div className="ml-4">
              <h3 className="font-semibold text-amber-900 text-lg">Important Notice</h3>
              <p className="text-amber-800 mt-1 text-base">
                Rideshare GST/HST registration is mandatory before your first trip.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Cards Grid */}
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          {cards.map((card, index) => (
            <motion.div
              key={card.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 + index * 0.1 }}
              className="bg-white rounded-xl shadow-md border border-gray-100 p-6 hover:shadow-xl hover:border-blue-200 transition-all duration-300"
            >
              <div className="border-b border-gray-100 pb-4 mb-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="h-12 w-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-md">
                      <card.icon className="h-6 w-6 text-white" />
                    </div>
                  </div>
                  <div className="ml-4">
                    <h3 className="text-lg font-semibold text-gray-900">{card.title}</h3>
                  </div>
                </div>
              </div>
              <div className="mt-4">{card.content}</div>
            </motion.div>
          ))}
        </div>

        {/* CRA Province Support Notice */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="bg-gradient-to-br from-blue-50 via-indigo-50 to-blue-50 border-2 border-blue-200 rounded-xl shadow-lg p-8"
        >
          <div className="text-center">
            <div className="inline-flex items-center justify-center h-16 w-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl mb-5 shadow-lg">
              <DocumentTextIcon className="h-9 w-9 text-white" />
            </div>
            <h3 className="text-2xl font-bold text-blue-900 mb-3">CRA-Compliant Tax Reporting</h3>
            <p className="text-blue-800 mb-6 text-base max-w-3xl mx-auto">
              Supported provinces: ON, AB, BC, MB. We map CRA GST34 Lines 90–115. Provincial PST/RST
              in BC/MB is a sunk cost (non-recoverable) but is deductible on income tax (T2125).
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 text-sm">
              <div className="bg-white rounded-xl p-5 shadow-md border border-blue-100 hover:shadow-lg transition-shadow">
                <strong className="text-blue-900 text-base block mb-2">ON (HST 13%):</strong>
                <p className="text-blue-700">ITC recoverable on full HST for eligible inputs.</p>
              </div>
              <div className="bg-white rounded-xl p-5 shadow-md border border-blue-100 hover:shadow-lg transition-shadow">
                <strong className="text-blue-900 text-base block mb-2">AB (GST 5%):</strong>
                <p className="text-blue-700">ITC recoverable on GST only.</p>
              </div>
              <div className="bg-white rounded-xl p-5 shadow-md border border-blue-100 hover:shadow-lg transition-shadow">
                <strong className="text-blue-900 text-base block mb-2">BC/MB (GST+PST/RST):</strong>
                <p className="text-blue-700">
                  ITC recoverable on GST only; PST/RST non-recoverable (sunk cost).
                </p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Additional Resources */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className="bg-gradient-to-br from-gray-50 to-slate-50 border border-gray-200 rounded-xl shadow-md p-8"
        >
          <div className="text-center">
            <div className="inline-flex items-center justify-center h-14 w-14 bg-gradient-to-br from-gray-400 to-gray-600 rounded-xl mb-4 shadow-md">
              <DocumentTextIcon className="h-8 w-8 text-white" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-3">Need More Help?</h3>
            <p className="text-gray-600 mb-5 text-base max-w-2xl mx-auto">
              For detailed CRA guidance on rideshare tax obligations, visit the official CRA website
              or consult a tax professional.
            </p>
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl p-4 text-left max-w-2xl mx-auto">
              <p className="text-sm text-blue-800">
                <strong>Reports tip:</strong> Exact keeps cents. CRA-Rounded shows what goes on your
                return.
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Learn;
